import { SearchQueryModelConverter } from '@neptune/search-query-domain';
import { searchLeaderboard } from '@neptune/shared/leaderboard-domain';

import {
  KnownAttributes,
  knownAttributesForExperimentIdentification,
} from 'domain/experiment/attribute/known-attributes';
import type { ExperimentState } from 'domain/experiment/experiment-state';

export type RunExperimentRaceWarningKind = 'multiple-active-runs-for-experiment';

export type RunExperimentRaceWarning = {
  activeRunCount: number;
  experimentName: string;
  kind: RunExperimentRaceWarningKind;
  message: string;
};

export type RunExperimentRaceDiagnostics = {
  activeRunState?: ExperimentState;
  warnings: RunExperimentRaceWarning[];
};

type ActiveRunsLookup = (
  projectIdentifier: string,
  experimentName: string,
) => Promise<{ activeRunCount: number }>;

export async function resolveRunExperimentRaceWarnings(
  {
    experimentName,
    projectIdentifier,
  }: {
    experimentName?: string;
    projectIdentifier: string;
  },
  lookupActiveRuns: ActiveRunsLookup = lookupActiveExperimentRuns,
): Promise<RunExperimentRaceDiagnostics> {
  if (!experimentName) {
    return { warnings: [] };
  }

  const { activeRunCount } = await lookupActiveRuns(projectIdentifier, experimentName);
  const activeRunState = resolveActiveRunState(activeRunCount);

  if (activeRunCount < 2) {
    return { activeRunState, warnings: [] };
  }

  return {
    activeRunState,
    warnings: [
      {
        activeRunCount,
        experimentName,
        kind: 'multiple-active-runs-for-experiment',
        message: `Multiple active runs use experiment name '${experimentName}'.`,
      },
    ],
  };
}

function resolveActiveRunState(activeRunCount: number): ExperimentState {
  return activeRunCount > 0 ? 'running' : 'idle';
}

async function lookupActiveExperimentRuns(
  projectIdentifier: string,
  experimentName: string,
): Promise<{ activeRunCount: number }> {
  const result = await searchLeaderboard({
    projectIdentifier,
    type: ['run'],
    attributesToFetch: knownAttributesForExperimentIdentification,
    experimentsOnly: false,
    pagination: {
      limit: 2,
      offset: 0,
    },
    query: SearchQueryModelConverter.convertSearchQueryToNql({
      criteria: [
        {
          attribute: KnownAttributes.Name,
          operator: '=',
          type: 'string',
          value: experimentName,
        },
        {
          attribute: KnownAttributes.State,
          operator: '=',
          type: 'experimentState',
          value: 'running',
        },
      ],
      operator: 'and',
    }),
  });

  return { activeRunCount: result.entries.length };
}
